import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import usePortvilleRoomAuth from '../../../../hooks/admin/usePortvilleRoomAuth';
import { usePortvilleBilling } from '../../../../hooks/admin/usePortvilleBilling';
import { usePortvilleRooms } from '../../../../hooks/admin/usePortvilleRooms';
import { useAuth } from '../../../../hooks/useAuth';
import { useToast } from '../../../../hooks/useToast';
import { useSearch } from '../../../../hooks/useSearch';
import { useBillingStatistics } from '../../../../hooks/useBillingStatistics';
import BillingSettingsForm from '../../../../components/admin/portville/billing/BillingSettingsForm';
import BillingSettingsTable from '../../../../components/admin/portville/billing/BillingSettingsTable';
import BillingSettingsHeader from '../../../../components/admin/portville/billing/BillingSettingsHeader';
import BillingSettingsFilters from '../../../../components/admin/portville/billing/BillingSettingsFilters';
import BillingStatisticsCards from '../../../../components/admin/portville/billing/BillingStatisticsCards';
import BillingAlerts from '../../../../components/admin/portville/billing/BillingAlerts';
import { portvilleBillingService } from '../../../../services/PortvilleBillingService';
import {
  PortvilleBillingSettings,
  BillingSettingsFormData,
  PortvilleBillingSettingsSnapshot
} from '../../../../types/portvilleBilling';

const BillingSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading: authLoading, hasAccess } = usePortvilleRoomAuth();
  const { allRooms, createFacility } = usePortvilleRooms();
  const {
    billingSettings,
    loading,
    connected,
    error,
    saveBillingSettings,
    refreshData
  } = usePortvilleBilling();

  // 커스텀 hooks
  const { toasts, showSuccess, showError, hideToast } = useToast();
  const pageStatistics = useBillingStatistics(allRooms, billingSettings);

  // 상태 관리
  const [showForm, setShowForm] = useState(false);
  const [editingSettings, setEditingSettings] = useState<PortvilleBillingSettings | undefined>(undefined);
  const [selectedRoomId, setSelectedRoomId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 타임라인 뷰 상태 관리
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineRoomId, setTimelineRoomId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [settingsSnapshot, setSettingsSnapshot] = useState<PortvilleBillingSettingsSnapshot | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // 검색 기능 - useSearch 훅 사용
  const searchFields = [
    { path: 'roomNumber' },
    { path: 'currentResident.name' },
    { path: 'currentResident.organization' },
    { path: 'facilityInfo.facilityName' },
    { path: 'facilityInfo.responsiblePerson.name' },
    { path: 'facilityInfo.responsiblePerson.organization' }
  ];

  const {
    searchTerm,
    filteredItems: filteredRooms,
    searchStats,
    handleSearchChange
  } = useSearch(allRooms || [], searchFields);

  // 권한 확인
  useEffect(() => {
    if (!authLoading && !hasAccess) {
      navigate('/admin/dashboard');
    }
  }, [authLoading, hasAccess, navigate]);

  // 설정 매핑 최적화 (Map 사용)
  const settingsMap = useMemo(() => {
    if (!billingSettings?.length) return new Map();
    return new Map(billingSettings.map(s => [s.roomId, s]));
  }, [billingSettings]);

  // 설정이 있는 호실 매핑 (최적화)
  const roomsWithSettings = useMemo(() => {
    if (!filteredRooms?.length) return [];

    return filteredRooms
      .filter(room => room !== null)
      .map(room => {
        const setting = settingsMap.get(room.id);
        return {
          ...room,
          billingSettings: setting
        };
      });
  }, [filteredRooms, settingsMap]);

  // 시설물 생성 처리 (useCallback으로 최적화)
  const handleCreateFacility = useCallback(async (facilityData: any) => {
    try {
      const roomId = await createFacility(facilityData);
      return roomId;
    } catch (error) {
      console.error('시설물 생성 실패:', error);
      throw error;
    }
  }, [createFacility]);

  // 폼 제출 처리 (useCallback으로 최적화)
  const handleFormSubmit = useCallback(async (formData: BillingSettingsFormData) => {
    if (!user?.uid) return;

    try {
      setIsSubmitting(true);
      const success = await saveBillingSettings(formData, user.uid);

      if (success) {
        setShowForm(false);
        setEditingSettings(undefined);
        setSelectedRoomId('');
        showSuccess('관리비 설정이 성공적으로 저장되었습니다.');
      }
    } catch (error) {
      console.error('관리비 설정 저장 실패:', error);
      showError('관리비 설정 저장에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsSubmitting(false);
    }
  }, [user?.uid, saveBillingSettings, showSuccess, showError]);

  // 폼 취소 (useCallback으로 최적화)
  const handleFormCancel = useCallback(() => {
    setShowForm(false);
    setEditingSettings(undefined);
    setSelectedRoomId('');
  }, []);

  // 새 설정 추가 (useCallback으로 최적화)
  const handleAddSettings = useCallback(() => {
    setEditingSettings(undefined);
    setSelectedRoomId('');
    setShowForm(true);
  }, []);

  // 설정 편집 (useCallback으로 최적화)
  const handleEditSettings = useCallback((settings: PortvilleBillingSettings) => {
    setEditingSettings(settings);
    setSelectedRoomId(settings.roomId);
    setShowForm(true);
  }, []);

  // 타임라인 뷰 열기
  const handleShowTimeline = useCallback(async (roomId: string) => {
    try {
      setTimelineLoading(true);
      setTimelineRoomId(roomId);
      setShowTimeline(true);

      // 현재 날짜로 스냅샷 조회
      const snapshot = await portvilleBillingService.getBillingSettingsAtDate(roomId, selectedDate);
      setSettingsSnapshot(snapshot);
    } catch (error) {
      console.error('타임라인 데이터 조회 실패:', error);
      showError('타임라인 데이터를 불러오는데 실패했습니다.');
    } finally {
      setTimelineLoading(false);
    }
  }, [selectedDate, showError]);

  // 타임라인 뷰 닫기
  const handleCloseTimeline = useCallback(() => {
    setShowTimeline(false);
    setTimelineRoomId('');
    setSettingsSnapshot(null);
  }, []);

  // 날짜 변경 시 스냅샷 재조회
  const handleDateChange = useCallback(async (newDate: Date) => {
    if (!timelineRoomId) return;

    try {
      setTimelineLoading(true);
      setSelectedDate(newDate);

      const snapshot = await portvilleBillingService.getBillingSettingsAtDate(timelineRoomId, newDate);
      setSettingsSnapshot(snapshot);
    } catch (error) {
      console.error('설정 스냅샷 조회 실패:', error);
      showError('해당 시점의 설정을 불러오는데 실패했습니다.');
    } finally {
      setTimelineLoading(false);
    }
  }, [timelineRoomId, showError]);

  // 로딩 중 표시
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">권한 확인 중...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* 헤더 */}
      <BillingSettingsHeader
        loading={loading}
        connected={connected}
        onRefresh={refreshData}
        onAddSettings={handleAddSettings}
      />

      {/* 알림 */}
      <BillingAlerts
        connected={connected}
        error={error}
        toasts={toasts}
        onHideToast={hideToast}
      />

      {/* 메인 콘텐츠 */}
      {!showForm && !showTimeline ? (
        <>
          {/* 통계 카드 */}
          <BillingStatisticsCards statistics={pageStatistics} />

          {/* 검색 */}
          <BillingSettingsFilters
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            searchStats={searchStats}
            disabled={loading}
          />

          {/* 설정 목록 테이블 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <h2 className="text-xl font-bold text-[#1e2c49] mb-4">관리비 설정 목록</h2>
              <div className="border-t border-gray-200 mb-6"></div>

              <BillingSettingsTable
                rooms={roomsWithSettings}
                onEdit={handleEditSettings}
                onShowTimeline={handleShowTimeline}
                loading={loading}
              />
            </div>
          </div>
        </>
      ) : showTimeline ? (
        /* 설정 타임라인 뷰 */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[#1e2c49]">
                설정 타임라인 - 호실 {roomsWithSettings.find(r => r.id === timelineRoomId)?.roomNumber}
              </h2>
              <button
                onClick={handleCloseTimeline}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                ✕ 닫기
              </button>
            </div>
            <div className="border-t border-gray-200 mb-6"></div>

            {/* 날짜 선택기 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                조회 시점 선택
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="date"
                  value={selectedDate.toISOString().split('T')[0]}
                  onChange={(e) => handleDateChange(new Date(e.target.value))}
                  className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="text-sm text-gray-500">
                  해당 시점에 유효했던 관리비 설정을 조회합니다
                </div>
              </div>
            </div>

            {/* 설정 스냅샷 정보 */}
            {timelineLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-500">설정 정보를 불러오는 중...</div>
              </div>
            ) : settingsSnapshot ? (
              <div className="space-y-6">
                {/* 스냅샷 메타 정보 */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="font-medium text-blue-900">
                      {settingsSnapshot.isCurrentSettings ? '현재 활성 설정' : '과거 설정'}
                    </span>
                  </div>
                  <div className="text-sm text-blue-800 space-y-1">
                    <div>설정 적용일: {settingsSnapshot.snapshotDate.toLocaleDateString()}</div>
                    <div>설정 버전: v{settingsSnapshot.settings.version}</div>
                    <div>변경 이력: {settingsSnapshot.historyCount}건</div>
                    {settingsSnapshot.nextChangeDate && (
                      <div>다음 변경일: {settingsSnapshot.nextChangeDate.toLocaleDateString()}</div>
                    )}
                  </div>
                </div>

                {/* 설정 상세 정보 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* 기본 정보 */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">기본 정보</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">호실번호:</span>
                        <span className="font-medium">{settingsSnapshot.settings.roomNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">계약자 연락처:</span>
                        <span className="font-medium">
                          {settingsSnapshot.settings.contactInfo?.contractorPhoneNumber || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">변경 사유:</span>
                        <span className="font-medium">
                          {settingsSnapshot.settings.changeReason || '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 요금 정보 */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">요금 정보</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">월세:</span>
                        <span className="font-medium">
                          {settingsSnapshot.settings.baseRates?.monthlyRent?.toLocaleString() || 0}원
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">관리비:</span>
                        <span className="font-medium">
                          {settingsSnapshot.settings.baseRates?.managementFee?.toLocaleString() || 0}원
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">통신비:</span>
                        <span className="font-medium">
                          {settingsSnapshot.settings.baseRates?.communicationFee?.toLocaleString() || 0}원
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 적용 기간 정보 */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">적용 기간</h3>
                  <div className="text-sm text-yellow-700">
                    <div>시작일: {settingsSnapshot.settings.effectiveFrom.toLocaleDateString()}</div>
                    {settingsSnapshot.settings.effectiveTo ? (
                      <div>종료일: {settingsSnapshot.settings.effectiveTo.toLocaleDateString()}</div>
                    ) : (
                      <div>종료일: 현재까지 적용</div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                해당 시점에 유효한 설정을 찾을 수 없습니다.
              </div>
            )}
          </div>
        </div>
      ) : (
        /* 설정 폼 */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <h2 className="text-xl font-bold text-[#1e2c49] mb-4">
              {editingSettings ? '관리비 설정 수정' : '관리비 설정 추가'}
            </h2>
            <div className="border-t border-gray-200 mb-6"></div>

            <BillingSettingsForm
              settings={editingSettings}
              rooms={allRooms}
              selectedRoomId={selectedRoomId}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={isSubmitting}
              onCreateFacility={handleCreateFacility}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingSettingsPage;
